import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, Tooltip, Table, TableHead, TableRow, TableCell,
  LinearProgress, Avatar, Tabs, Tab,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  MY_VIP_ESCORTS_QUERY,
  CREATE_VIP_ESCORT_MUTATION,
  UPDATE_VIP_ESCORT_MUTATION,
  DELETE_VIP_ESCORT_MUTATION,
  MY_CEREMONIES_QUERY,
  MY_GUESTS_QUERY,
} from '../graphql/queries';

const ESCORT_ROLES = ['usher', 'greeter', 'coordinator', 'dedicated_staff'];
const STATUS_COLOR = { assigned: 'warning', in_progress: 'info', completed: 'success', cancelled: 'error' };

const EMPTY_FORM = {
  guestId: '', ceremonyId: '', escortName: '', escortRole: 'usher',
  fromLocation: '', toLocation: '', escortTime: '', notes: '',
};

export default function VipEscortPage() {
  const [filterCeremony, setFilterCeremony] = useState('');
  const [tab, setTab]         = useState(0);
  const [dialog, setDialog]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [error, setError]     = useState('');

  const { data: escortData, loading, refetch } = useQuery(MY_VIP_ESCORTS_QUERY, {
    variables: filterCeremony ? { ceremonyId: filterCeremony } : {},
    fetchPolicy: 'cache-and-network',
  });
  const { data: ceremonyData } = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: guestData }    = useQuery(MY_GUESTS_QUERY,     { fetchPolicy: 'cache-and-network' });

  const escorts    = escortData?.myVipEscorts       || [];
  const ceremonies = ceremonyData?.myCeremonies?.filter(c => c.status !== 'cancelled') || [];
  const vipGuests  = (guestData?.myGuests           || []).filter(g => g.isVip);
  const allGuests  = guestData?.myGuests             || [];

  // Stats
  const stats = {
    total:      escorts.length,
    assigned:   escorts.filter(e => e.status === 'assigned').length,
    inProgress: escorts.filter(e => e.status === 'in_progress').length,
    completed:  escorts.filter(e => e.status === 'completed').length,
  };

  const [createEscort] = useMutation(CREATE_VIP_ESCORT_MUTATION, {
    onCompleted: () => { setDialog(false); setForm(EMPTY_FORM); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [updateEscort] = useMutation(UPDATE_VIP_ESCORT_MUTATION, {
    onCompleted: () => { setDialog(false); setEditing(null); refetch(); setError(''); },
    onError: e => setError(e.message),
  });
  const [deleteEscort] = useMutation(DELETE_VIP_ESCORT_MUTATION, {
    onCompleted: () => refetch(),
  });

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialog(true); };
  const openEdit   = (e) => {
    setEditing(e);
    setForm({
      guestId: e.guestId || '', ceremonyId: e.ceremonyId || '',
      escortName: e.escortName || '', escortRole: e.escortRole || 'usher',
      fromLocation: e.fromLocation || '', toLocation: e.toLocation || '',
      escortTime: e.escortTime || '', notes: e.notes || '',
    });
    setDialog(true);
  };
  const handleSave = () => {
    if (editing) updateEscort({ variables: { id: editing.id, escortName: form.escortName, escortRole: form.escortRole, fromLocation: form.fromLocation, toLocation: form.toLocation, escortTime: form.escortTime, notes: form.notes } });
    else createEscort({ variables: { ...form, guestId: form.guestId || null, ceremonyId: form.ceremonyId || null } });
  };
  const quickStatus = (escort, status) => updateEscort({ variables: { id: escort.id, status } });

  const filteredEscorts = tab === 1
    ? escorts.filter(e => e.status !== 'completed' && e.status !== 'cancelled')
    : tab === 2
    ? escorts.filter(e => e.status === 'completed')
    : escorts;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>VIP Escort Workflow</Typography>
          <Typography variant="body2" color="text.secondary">Manage escorts for VIP guests across all ceremonies.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Assign Escort</Button>
      </Stack>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Escorts',  value: stats.total,       color: 'text.primary'  },
          { label: 'Assigned',       value: stats.assigned,    color: 'warning.main'  },
          { label: 'In Progress',    value: stats.inProgress,  color: 'info.main'     },
          { label: 'Completed',      value: stats.completed,   color: 'success.main'  },
        ].map(s => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card><CardContent sx={{ textAlign: 'center', py: '12px !important' }}>
              <Typography variant="h4" fontWeight={700} color={s.color}>{s.value}</Typography>
              <Typography variant="caption" color="text.secondary">{s.label}</Typography>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>

      {/* Ceremony filter */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
        <Chip label="All Ceremonies" onClick={() => setFilterCeremony('')} color={!filterCeremony ? 'primary' : 'default'} variant={!filterCeremony ? 'filled' : 'outlined'} />
        {ceremonies.map(c => (
          <Chip key={c.id} label={c.name} onClick={() => setFilterCeremony(c.id === filterCeremony ? '' : c.id)} color={filterCeremony === c.id ? 'primary' : 'default'} variant={filterCeremony === c.id ? 'filled' : 'outlined'} />
        ))}
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`All (${escorts.length})`} />
        <Tab label={`Active (${stats.assigned + stats.inProgress})`} />
        <Tab label={`Completed (${stats.completed})`} />
      </Tabs>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {filteredEscorts.length === 0 && !loading && (
        <Alert severity="info">No escorts found. Assign escorts to VIP guests using the button above.</Alert>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            {['VIP Guest', 'Ceremony', 'Escort Person', 'Role', 'Route', 'Time', 'Status', 'Actions'].map(h => (
              <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredEscorts.map(e => (
            <TableRow key={e.id} hover>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'warning.main', fontSize: '0.7rem' }}>
                    <StarIcon sx={{ fontSize: 14 }} />
                  </Avatar>
                  <Typography variant="body2" fontWeight={600}>{e.guest?.name || '—'}</Typography>
                </Stack>
              </TableCell>
              <TableCell><Typography variant="caption">{e.ceremony?.name || '—'}</Typography></TableCell>
              <TableCell>{e.escortName || '—'}</TableCell>
              <TableCell>
                <Chip
                  label={e.escortRole?.replace('_', ' ') || 'usher'}
                  size="small"
                  icon={<DirectionsWalkIcon />}
                />
              </TableCell>
              <TableCell>
                {e.fromLocation && e.toLocation
                  ? <Typography variant="caption">{e.fromLocation} to {e.toLocation}</Typography>
                  : e.fromLocation || e.toLocation || '—'}
              </TableCell>
              <TableCell>{e.escortTime || '—'}</TableCell>
              <TableCell>
                <Chip label={e.status} size="small" color={STATUS_COLOR[e.status] || 'default'} />
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.25}>
                  {e.status === 'assigned' && (
                    <Tooltip title="Mark In Progress">
                      <IconButton size="small" color="info" onClick={() => quickStatus(e, 'in_progress')}>
                        <DirectionsWalkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {e.status === 'in_progress' && (
                    <Tooltip title="Mark Completed">
                      <IconButton size="small" color="success" onClick={() => quickStatus(e, 'completed')}>
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(e)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => deleteEscort({ variables: { id: e.id } })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Assign Escort Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Escort Assignment' : 'Assign VIP Escort'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!editing && (
              <Stack direction="row" spacing={2}>
                <TextField
                  select label="VIP Guest" value={form.guestId}
                  onChange={e => setForm(f => ({ ...f, guestId: e.target.value }))}
                  fullWidth required
                  helperText={allGuests.filter(g => g.isVip).length === 0 ? 'No VIP guests found — all guest types shown' : `${vipGuests.length} VIP guests available`}
                >
                  {(vipGuests.length > 0 ? vipGuests : allGuests).map(g => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name} {g.isVip ? '(VIP)' : ''} {g.side ? `(${g.side})` : ''}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select label="Ceremony" value={form.ceremonyId}
                  onChange={e => setForm(f => ({ ...f, ceremonyId: e.target.value }))}
                  fullWidth required
                >
                  {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
              </Stack>
            )}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Escort Person Name" value={form.escortName}
                onChange={e => setForm(f => ({ ...f, escortName: e.target.value }))}
                fullWidth placeholder="Staff name responsible for escort"
              />
              <TextField
                select label="Escort Role" value={form.escortRole}
                onChange={e => setForm(f => ({ ...f, escortRole: e.target.value }))}
                fullWidth
              >
                {ESCORT_ROLES.map(r => (
                  <MenuItem key={r} value={r}>{r.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="From Location" value={form.fromLocation}
                onChange={e => setForm(f => ({ ...f, fromLocation: e.target.value }))}
                fullWidth placeholder="e.g. Main Gate, Parking"
              />
              <TextField
                label="To Location" value={form.toLocation}
                onChange={e => setForm(f => ({ ...f, toLocation: e.target.value }))}
                fullWidth placeholder="e.g. Mandap, VIP Table"
              />
            </Stack>
            <TextField
              label="Escort Time" value={form.escortTime}
              onChange={e => setForm(f => ({ ...f, escortTime: e.target.value }))}
              fullWidth placeholder="e.g. 10:30 AM, After arrival"
            />
            <TextField
              label="Notes" value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              fullWidth multiline rows={2}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialog(false); setError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
